/**
 * 토큰 재발급 조정자 — **재발급은 여기 한 곳에서만 일어난다**
 *
 * ━━━ 왜 만들었나 (2026-09-21, 계획 심사 지적 11건) ━━━
 *
 * 재발급을 부르는 곳이 **셋**이었고 서로를 몰랐다.
 *
 *   ① `fetchWithAuth`            (`utils/authHeaders.ts`)
 *   ② axios 응답 가로채기        (`services/api/apiClient.ts`)
 *   ③ `authService.refreshToken` (`services/auth/authService.ts`)
 *
 * 그래서 네 가지가 동시에 잘못돼 있었다.
 *
 *   ⛔ **서버 사정을 인증 만료로 읽었다.** 5xx·타임아웃·끊김이 「재발급권 만료」와
 *      같은 길로 가서 **살아 있는 재발급권까지 지웠다.** 서버가 흔들리면 로그아웃됐다.
 *
 *   ⛔ **성공이 화면에 전해지지 않았다.** ①② 는 토큰만 저장하고 `session_expiry` 는
 *      안 고쳤다. 그래서 재발급에 **성공하고도** 화면이 옛 시각에 로그아웃시켰다.
 *
 *   ⛔ **실패했을 때 대기열을 정산하지 않았다.** ② 는 재발급 중 들어온 요청을 재워 두고
 *      **성공할 때만** 깨웠다. 실패가 곧 로그아웃이던 시절엔 안 보였지만,
 *      로그아웃하지 않게 고치는 순간 그 요청들이 **영영 안 깨어난다.**
 *
 *   ⛔ **되돌이가 생긴다.** 만료된 출입증이 모든 요청에 계속 붙으면
 *      API 가 401 을 낼 때마다 재발급을 다시 부른다.
 *
 * ━━━ 그래서 이 파일이 지키는 약속 ━━━
 *
 *   1. 재발급 요청은 **동시에 하나만** 나간다. 나머지는 같은 약속을 기다린다.
 *   2. 실패 이유를 **가른다.** 400·401 만 「재발급권 무효」다.
 *   3. 성공하면 토큰·재발급권·만료시각·사용자정보를 **한 번에** 저장하고 알린다.
 *   4. 성공이든 실패든 **기다리던 것들을 반드시 결말짓는다.**
 *   5. 서버 사정으로 실패하면 **뒤에서 정해진 횟수만큼** 다시 시도하고,
 *      그동안 새 요청은 재발급을 **또 부르지 않는다.**
 *   6. 5분이 지나면 **멈추고 안내**한다. ⛔ 그래도 토큰은 지우지 않는다.
 */

// ⛔ `apiClient` 에서 가져오면 순환 참조가 된다(apiClient 가 이 파일을 쓴다).
//    같은 식으로 여기서 직접 만든다 — 값이 갈라지지 않도록 같은 환경변수를 본다.
// ⛔ `apiClient` 가 아니라 **맨 axios** 를 쓴다.
//    apiClient 를 쓰면 그 가로채기가 401 을 보고 또 재발급을 불러 되돌이가 된다.
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user_info';
const SESSION_EXPIRY_KEY = 'session_expiry';

/** 계정 자체가 막힌 사유들 — 재발급을 더 시도해봐야 소용없다. */
const 차단코드들 = ['ACCOUNT_RESIGNED', 'ACCOUNT_PENDING_APPROVAL', 'ACCOUNT_REJECTED'];

/** 뒤에서 다시 시도하는 간격(밀리초). 마지막 값이 상한이다. */
const 재시도간격 = [2_000, 4_000, 8_000, 16_000, 30_000];

/** 뒤에서 다시 시도하는 총 시간 상한. 이 시간이 지나면 멈추고 안내한다. */
const 재시도상한 = 5 * 60 * 1000;

/**
 * 재발급 요청 한 번의 제한 시간.
 *
 * ⛔ 없으면 **응답이 영영 안 올 때 아무 일도 일어나지 않는다** — 최종 심사 [F-1].
 *    약속이 pending 으로 남아 `catch` 가 안 돌고, 그러면 재시도도·5분 상한도·
 *    대기열 정산도 **전부 작동하지 않는다.** 「응답 없음」이 제일 위험한 경우다.
 */
const 요청제한시간 = 30_000;

export type RefreshOutcome =
  /** 재발급 성공 */
  | { kind: 'ok'; token: string }
  /** 재발급권이 진짜 무효다 (400·401) → 호출측이 로그아웃한다 */
  | { kind: 'invalid' }
  /** 계정이 막혔다 (퇴사·승인대기·거부) → 사유를 보이고 로그아웃한다 */
  | { kind: 'blocked'; reason: string }
  /**
   * SMS 재인증 창이 지났다 → ⛔ **로그아웃이 아니다.** 인증 창을 띄운다.
   *
   * ⛔ `blocked` 로 묶으면 안 된다. `blocked` 의 소비처는 전부 로그아웃시킨다 —
   *    그러면 이 작업이 막으려던 「부당한 로그아웃」을 우리가 다시 만드는 것이다.
   */
  | { kind: 'sms_required'; challenge: string; phoneNumber: string | null }
  /** 서버 사정이다 (5xx·429·끊김) → ⛔ 토큰을 지우지 않는다 */
  | { kind: 'unavailable' };

/**
 * ⛔ **갈래를 빠뜨리면 여기서 컴파일이 멈춘다.**
 *
 * 왜 필요한가 (2026-09-22 · Codex 심사 [C-1]):
 *   `RefreshOutcome` 은 판별 유니온이지만, 소비처가 `if / else if` 사슬이면
 *   **타입 검사는 아무것도 막지 못한다.** 갈래를 더해도 조용히 통과하고
 *   동작은 사슬 끝(대개 로그아웃)으로 떨어진다.
 *
 *   실제로 `sms_required` 를 더했을 때 타입 오류가 **0건**이었다 —
 *   그대로 뒀으면 SMS 재인증이 «로그아웃»이 될 뻔했다.
 *
 * 그래서 소비처를 `switch` 로 바꾸고 `default` 에서 이것을 부른다.
 * 다음에 갈래가 늘면 **컴파일이 실패해서** 빠뜨릴 수 없다.
 */
export function assertNever(x: never): never {
  throw new Error(`처리하지 않은 갈래: ${JSON.stringify(x)}`);
}

/** 화면에 보여 줄 연결 상태. */
export type ConnectionState = 'ok' | 'retrying' | 'gave-up';

// ── 모듈 상태 ────────────────────────────────────────────────
let 진행중: Promise<RefreshOutcome> | null = null;
let 연결상태: ConnectionState = 'ok';
let 재시도타이머: ReturnType<typeof setTimeout> | null = null;
let 재시도시작시각 = 0;
let 재시도횟수 = 0;

/**
 * 세대 번호.
 *
 * ⛔ 로그아웃하거나 상태를 푸는 순간 올린다 — 최종 심사 [F-5].
 *    올리지 않으면, 이미 로그아웃한 뒤에 **늦게 도착한 성공 응답**이
 *    토큰을 다시 저장하고 화면에 알려 **세션을 되살린다.**
 */
let 세대 = 0;

type 갱신알림 = (info: { token: string; expiresAt: Date }) => void;
const 성공구독자 = new Set<갱신알림>();
const 상태구독자 = new Set<(s: ConnectionState) => void>();

// ── 저장 ────────────────────────────────────────────────────

function 읽기(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function 쓰기(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 저장이 막혀 있어도 이번 요청 자체는 진행한다 */
  }
}

export function getStoredRefreshToken(): string | null {
  return 읽기(REFRESH_TOKEN_KEY);
}

/**
 * 성공한 재발급 결과를 **한 번에** 저장한다.
 *
 * ⛔ 종전에는 경로마다 저장하는 항목이 달랐다. 그래서 어떤 경로로 갱신됐는지에 따라
 *    `session_expiry` 가 갱신되기도 하고 안 되기도 했다. 여기로 모은 이유다.
 */
function 성공저장(data: {
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: unknown;
}): Date {
  쓰기(AUTH_TOKEN_KEY, data.token);
  if (data.refreshToken) 쓰기(REFRESH_TOKEN_KEY, data.refreshToken);
  if (data.user) {
    try {
      쓰기(USER_KEY, JSON.stringify(data.user));
    } catch {
      /* 직렬화 실패는 무시한다 — 토큰 저장이 더 중요하다 */
    }
  }

  // ⛔ `expiresIn` 이 없으면 **기존 만료시각을 유지**한다.
  //    0 이나 기본값으로 덮으면 멀쩡한 세션을 스스로 끊는다.
  let 만료: Date;
  if (typeof data.expiresIn === 'number' && data.expiresIn > 0) {
    만료 = new Date(Date.now() + data.expiresIn * 1000);
    쓰기(SESSION_EXPIRY_KEY, 만료.toISOString());
  } else {
    const 기존 = 읽기(SESSION_EXPIRY_KEY);
    만료 = 기존 ? new Date(기존) : new Date(Date.now() + 3600 * 1000);
  }

  성공구독자.forEach((fn) => {
    try {
      fn({ token: data.token, expiresAt: 만료 });
    } catch {
      /* 구독자 하나가 터져도 나머지는 알려야 한다 */
    }
  });

  return 만료;
}

// ── 실패 분류 ────────────────────────────────────────────────

/**
 * 실패를 **이유별로 가른다.** 이것이 이 파일의 핵심이다.
 *
 * ⛔ 응답이 아예 없는 경우(네트워크 끊김)는 상태 코드가 없다.
 *    그때의 기본값은 **`unavailable`(토큰 유지)** 이다.
 *    fail-open 이 아니라 fail-safe 다 — 토큰을 남겨도 서버가 다시 검증한다.
 */
function 분류(error: unknown): RefreshOutcome {
  const resp = (error as { response?: { status?: number; data?: Record<string, any> } })?.response;

  const 코드 = resp?.data?.code ?? resp?.data?.error?.code;

  // ⛔ SMS 재인증은 «계정이 막힌 것»이 아니다. 차단 판정보다 **먼저** 갈라낸다.
  //    status 보다 code 를 먼저 보는 이 구조 덕분에, 서버가 403 이든 401 이든 상관없다.
  if (String(코드) === 'SMS_REAUTH_REQUIRED') {
    return {
      kind: 'sms_required',
      challenge: String(resp?.data?.smsChallenge ?? ''),
      phoneNumber: resp?.data?.phoneNumber ?? null,
    };
  }

  if (차단코드들.includes(String(코드))) {
    return {
      kind: 'blocked',
      reason:
        resp?.data?.message ??
        resp?.data?.error?.message ??
        '계정을 사용할 수 없습니다. 관리자에게 문의하세요.',
    };
  }

  const status = resp?.status;
  if (status === 400 || status === 401) return { kind: 'invalid' };

  // 429·5xx·그 밖의 모든 것(응답 없음 포함) → 서버 사정
  return { kind: 'unavailable' };
}

/**
 * 이 오류가 **서버 사정**인가 (5xx·429·응답 없음).
 *
 * ⛔ 재발급 말고 다른 요청(`/auth/me` 등)의 실패도 같은 잣대로 재야 한다 — 최종 심사 [F-12].
 *    잣대가 갈리면, 같은 장애인데 한쪽은 «서버 사정», 다른 쪽은 «인증 만료» 로 읽힌다.
 */
export function isServerSideFailure(error: unknown): boolean {
  return 분류(error).kind === 'unavailable';
}

/** 429 응답이 알려 준 대기 시간(밀리초). 없으면 null. */
function 서버가정한대기(error: unknown): number | null {
  const resp = (error as { response?: { status?: number; data?: Record<string, any> } })?.response;
  if (resp?.status !== 429) return null;
  // ⛔ 실제 서버는 `error.retryAfter` 에 싣는다 (`rate-limit.js:89-95`) — 최종 심사 [F-4].
  //    최상위만 보면 60초 안내를 무시하고 2초 뒤에 다시 두드린다.
  const sec = resp?.data?.error?.retryAfter ?? resp?.data?.retryAfter;
  return typeof sec === 'number' && sec > 0 ? sec * 1000 : null;
}

// ── 연결 상태 ────────────────────────────────────────────────

function 상태바꾸기(s: ConnectionState): void {
  if (연결상태 === s) return;
  연결상태 = s;
  상태구독자.forEach((fn) => {
    try {
      fn(s);
    } catch {
      /* noop */
    }
  });
}

export function getConnectionState(): ConnectionState {
  return 연결상태;
}

export function subscribeConnection(fn: (s: ConnectionState) => void): () => void {
  상태구독자.add(fn);
  return () => {
    상태구독자.delete(fn);
  };
}

export function subscribeRefreshSuccess(fn: 갱신알림): () => void {
  성공구독자.add(fn);
  return () => {
    성공구독자.delete(fn);
  };
}

// ── 실제 호출 ────────────────────────────────────────────────

async function 한번부르기(): Promise<RefreshOutcome> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return { kind: 'invalid' };

  const 내세대 = 세대;

  try {
    const response = await axios.post<{
      success: boolean;
      token: string;
      refreshToken?: string;
      expiresIn?: number;
      user?: unknown;
    }>(`${API_BASE_URL}/auth/refresh`, { refreshToken }, { timeout: 요청제한시간 });

    const data = response.data;
    if (!data?.token) {
      // 200 인데 토큰이 없다 — 서버 계약 위반이다.
      // ⛔ 이것을 성공으로 처리하면 `Bearer undefined` 가 만들어진다.
      return { kind: 'invalid' };
    }

    // ⛔ 기다리는 사이에 로그아웃했는가. 그랬다면 **아무것도 하지 않는다** — [F-5].
    //    저장하면 방금 지운 토큰이 되살아나고, 알리면 화면이 다시 로그인 상태가 된다.
    if (내세대 !== 세대) return { kind: 'unavailable' };

    성공저장(data);
    return { kind: 'ok', token: data.token };
  } catch (error) {
    const outcome = 분류(error);
    if (outcome.kind === 'unavailable') {
      뒤에서다시시도(서버가정한대기(error));
    }
    return outcome;
  }
}

/**
 * 뒤에서 다시 시도한다.
 *
 * ⛔ 이미 돌고 있으면 **새로 시작하지 않는다.** 이것이 되돌이를 막는 자리다.
 * ⛔ 총 5분이 지나면 멈춘다. 끝나지 않는 「연결 확인 중」은 로그아웃보다 나쁘다.
 */
function 뒤에서다시시도(서버대기: number | null): void {
  if (재시도타이머) return; // 이미 돌고 있다

  if (재시도시작시각 === 0) 재시도시작시각 = Date.now();
  상태바꾸기('retrying');

  const 경과 = Date.now() - 재시도시작시각;
  if (경과 >= 재시도상한) {
    재시도멈춤('gave-up');
    return;
  }

  const 기본간격 = 재시도간격[Math.min(재시도횟수, 재시도간격.length - 1)];
  const 간격 = 서버대기 ?? 기본간격;
  재시도횟수 += 1;

  재시도타이머 = setTimeout(() => {
    재시도타이머 = null;
    void (async () => {
      const outcome = await requestRefresh({ 내부재시도: true });
      switch (outcome.kind) {
        case 'ok':
          재시도멈춤('ok');
          break;
        case 'invalid':
        case 'blocked':
          // 더 시도해도 소용없다. 멈추되 상태는 정상으로 되돌린다 —
          // 로그아웃 여부는 이 결과를 받은 호출측이 판단한다.
          재시도멈춤('ok');
          break;
        case 'sms_required':
          // ⛔ 여기서 계속 재시도하면 5분 동안 같은 거절을 반복한다.
          //    사람이 SMS 를 마쳐야 풀리는 것이라 기다림이 아니라 **멈춤**이 맞다.
          재시도멈춤('ok');
          break;
        case 'unavailable':
          // `한번부르기` 안에서 다음 회차가 다시 잡힌다
          break;
        default:
          assertNever(outcome);
      }
    })();
  }, 간격);
}

function 재시도멈춤(상태: ConnectionState): void {
  if (재시도타이머) {
    clearTimeout(재시도타이머);
    재시도타이머 = null;
  }
  재시도시작시각 = 0;
  재시도횟수 = 0;
  상태바꾸기(상태);
}

/**
 * 재발급을 요청한다. **모든 경로가 이 함수만 부른다.**
 *
 * 동시에 여러 곳에서 불러도 실제 요청은 하나만 나가고, 나머지는 같은 결과를 받는다.
 */
export async function requestRefresh(
  opts: { 내부재시도?: boolean } = {},
): Promise<RefreshOutcome> {
  // ⛔ 뒤에서 다시 시도하는 중이면 새 요청을 내보내지 않는다.
  //    이것이 없으면 API 가 401 을 낼 때마다 재발급이 나가 한도를 포화시킨다.
  if (!opts.내부재시도 && 재시도타이머) return { kind: 'unavailable' };

  // 5분을 다 쓰고 포기한 상태에서도 새 요청을 내보내지 않는다.
  // 사용자가 「다시 로그인」을 누를 때까지 조용히 있는다.
  if (!opts.내부재시도 && 연결상태 === 'gave-up') return { kind: 'unavailable' };

  if (진행중) return 진행중;

  진행중 = 한번부르기().finally(() => {
    진행중 = null;
  });

  return 진행중;
}

/**
 * 로그아웃·「다시 로그인」 때 부른다.
 *
 * 멈춰 둔 재시도를 풀고, **세대를 올려** 진행 중이던 요청의 결과를 버린다.
 */
export function resetConnectionState(): void {
  세대 += 1;
  진행중 = null;     // 새 요청은 새 약속으로 시작한다
  재시도멈춤('ok');
}

/** 검사용 — 모듈 상태를 비운다. 운영 코드에서는 쓰지 않는다. */
export function _resetForTest(): void {
  세대 += 1;
  진행중 = null;
  재시도멈춤('ok');
  성공구독자.clear();
  상태구독자.clear();
}
